/**
 * クッキー操作ユーティリティ
 * バックエンドAPIと連携してクッキーを管理
 */

/**
 * クッキーに値を設定（クライアントサイド）
 * @param {string} name - クッキー名
 * @param {string} value - 保存する値
 * @param {number} days - 有効期限(日数)。省略時はセッションクッキー
 * @param {object} options - 追加オプション (path, secure, sameSite)
 */
export const setCookie = (name, value, days = null, options = {}) => {
  try {
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    
    // 有効期限の設定
    if (days !== null && days !== undefined) {
      const date = new Date();
      // 🔧 修正: ミリ秒単位で正確に計算
      const milliseconds = days * 24 * 60 * 60 * 1000;
      date.setTime(date.getTime() + milliseconds);
      cookieString += `; expires=${date.toUTCString()}`;
      
    }
    
    // パスの設定(デフォルトは'/')
    cookieString += `; path=${options.path || '/'}`;
    
    // Secure属性(HTTPS環境では推奨)
    if (options.secure || window.location.protocol === 'https:') {
      cookieString += '; secure';
    }
    
    // SameSite属性(CSRF対策)
    cookieString += `; SameSite=${options.sameSite || 'Lax'}`;
    
    document.cookie = cookieString;

    return true;
  } catch (error) {
    console.error('クッキーの設定に失敗:', error);
    return false;
  }
};

/**
 * クッキーから値を取得
 * @param {string} name - クッキー名
 * @returns {string|null} - クッキーの値、存在しない場合はnull
 */
export const getCookie = (name) => {
  try {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  } catch (error) {
    console.error('クッキーの取得に失敗:', error);
    return null;
  }
};

/**
 * クッキーを削除
 * @param {string} name - クッキー名
 * @param {object} options - オプション (path)
 */
export const removeCookie = (name, options = {}) => {
  try {
    setCookie(name, '', -1, options);
    return true;
  } catch (error) {
    console.error('クッキーの削除に失敗:', error);
    return false;
  }
};

/**
 * JSONオブジェクトをクッキーに保存
 * @param {string} name - クッキー名
 * @param {object} obj - 保存するオブジェクト
 * @param {number} days - 有効期限(日数)
 * @param {object} options - 追加オプション
 */
export const setJSONCookie = (name, obj, days = null, options = {}) => {
  try {
    const jsonString = JSON.stringify(obj);
    return setCookie(name, jsonString, days, options);
  } catch (error) {
    console.error('JSONクッキーの設定に失敗:', error);
    return false;
  }
};

/**
 * JSONオブジェクトをクッキーから取得
 * @param {string} name - クッキー名
 * @returns {object|null} - パースされたオブジェクト、失敗時はnull
 */
export const getJSONCookie = (name) => {
  try {
    const value = getCookie(name);
    if (!value) return null;
    return JSON.parse(value);
  } catch (error) {
    console.error('JSONクッキーの取得に失敗:', error);
    return null;
  }
};

/**
 * 複数のクッキーを一括削除
 * @param {string[]} names - クッキー名の配列
 * @param {object} options - オプション
 */
export const removeMultipleCookies = (names, options = {}) => {
  names.forEach(name => removeCookie(name, options));
};